import { path, pick, values, groupWith } from 'ramda'
import { throttle } from 'lodash'
import { useEffect, useState, useMemo, Fragment, useReducer, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useLocation } from 'react-router'
import {
  Box,
  ButtonLink,
  Button,
  Text,
  Stack,
  OverflowMenu,
  MenuItemLink,
  TooltipRenderer,
  useDialogControllers
} from '@machobear/design-system'
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  StyledTableCell,
  SingleTableRow,
  Accordion,
  AccordionContent,
  AccordionTableRow,
  AccordionHeader,
  TableHeaderText,
  MobileRowWrapper,
  MobileUnitText,
  MobileUnitDetails
} from '@/components/listings-table'
import config from '@/config'
import PageTitle from '@/components/page-title'
import useMediaQuery from '@/hooks/use-media-query'
import { select } from '@/store'
import WarningModal from '@/components/modals/warning-modal'
import { colors, breakpoints, structure } from '@/styles'
import formatDate from '@/utils/format-date'
import { prefixUnitNumber, formatListingPrice } from '@/utils/listings'
import { WarningLarge } from '@/components/icons'
import useEventListener from '@/hooks/use-event-listener'
import { LoadingCircle } from '@/components/loading-circle'
import { NoRentalsComponent, RentalsEmptyListComponent } from '@/components/table-list'
import { sortStrings, sortNumbers, sortDates } from '@/utils/sortUtils'
import MyRentalsHeader from './my-rentals-header'
import SkeletonState from './rentals-skeleton'

const { APP_PATH } = config

const canViewDetail = state => ['draft', 'history'].indexOf(state) < 0

const Wrapper = styled.div`
  min-height: 100%;
  background-color: ${colors.white};
  padding: 0 0 200px;
  flex: 1;
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    padding: 0 0 125px;
  }
`

const TableOutterWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`

const TableWrapper = styled.div`
  flex: 1;
  max-width: 1130px;
  margin: 0 50px;
  height: fit-content;

  @media only screen and (max-width: ${breakpoints.desktop}) {
    margin: 0 30px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0;
  }
`

const TableBodyContainer = styled(TableBody)`
  & ${TableRow} {
    border-bottom: 1px solid ${colors.grey};
  }
  & ${TableRow}:last-child {
    border-bottom: 0px solid ${colors.grey};
  }
`

const getContractAddress = contract => {
  const { unit_number, address, city, state } = contract
  const unitNumber = unit_number ? `${prefixUnitNumber(unit_number)} - ` : ''
  const fullAddress = `${address}, ${city}, ${state}`
  return (
    <>
      <Text size="standard">
        {unitNumber}
        {fullAddress}
      </Text>
    </>
  )
}

const LoadingState = () => {
  return (
    <Box display="flex" justifyContent="center" padding="xlarge">
      <LoadingCircle />
    </Box>
  )
}

const getBuildingTitle = building => {
  const count = building.length
  const buildingInfo = building[0]
  return (
    <Stack space="medium">
      <Text weight="strong" size="small">
        {buildingInfo.building_name} ({count} units)
      </Text>
      <Text size="small">{`${buildingInfo.address}, ${buildingInfo.city}${
        buildingInfo.state === null ? '' : `, ${buildingInfo.state}`
      }`}</Text>
    </Stack>
  )
}

const OptionsMenu = ({ contract }) => {
  const { t } = useTranslation()
  return (
    <Box marginLeft="gutter">
      <OverflowMenu label="Options" id="options">
        <MenuItemLink target="_blank" href={`/chat`}>
          {t('l.tenants.list.chat.text', 'Chat')}
        </MenuItemLink>
      </OverflowMenu>
    </Box>
  )
}

const RenderMobileRentalRow = ({ groupedListing, contract }) => {
  const { t } = useTranslation()
  return (
    <>
      <StyledTableCell width="85%" colSpan={8}>
        <MobileRowWrapper indent={groupedListing}>
          <MobileUnitText>
            {groupedListing ? (
              <Text size="standard">{contract.unit_number}</Text>
            ) : (
              getContractAddress(contract)
            )}
          </MobileUnitText>
          <MobileUnitDetails>
            <p>
              <Text size="small">
                {t('l.rentals.table.mobile.start.text', 'Start:')}{' '}
                {formatDate(contract.start_date, 'MMM Do, YYYY') ||
                  t('b.components.view.not_avaiable.text', 'N/A')}
              </Text>
            </p>
            <p>
              <Text size="small">
                {('l.rentals.table.mobile.end.text', 'End:')}{' '}
                {formatDate(contract.end_date, 'MMM Do, YYYY') ||
                  t('b.components.view.not_avaiable.text', 'N/A')}
              </Text>
            </p>
          </MobileUnitDetails>
        </MobileRowWrapper>
      </StyledTableCell>
      <StyledTableCell width="15%" colSpan={1}>
        <MobileUnitText>
          <OptionsMenu contract={contract} />
        </MobileUnitText>
      </StyledTableCell>
    </>
  )
}

const RenderRentalRow = ({ groupedListing, contract }) => {
  const { t } = useTranslation()
  const isDesktop = useMediaQuery(`(max-width: ${breakpoints.desktop})`)
  return (
    <>
      <StyledTableCell justifyContent="flex-start" blueBorder={groupedListing}>
        <Box
          paddingLeft={groupedListing ? 'xlarge' : 'xsmall'}
          paddingRight="xsmall"
          paddingY="xsmall"
        >
          {groupedListing ? (
            <Text size="standard">{contract.unit_number}</Text>
          ) : (
            getContractAddress(contract)
          )}
        </Box>
      </StyledTableCell>
      <StyledTableCell justifyContent="flex-start">
        <Box padding="xsmall">
          <Text size="standard">{formatListingPrice(contract.price)}</Text>
        </Box>
      </StyledTableCell>
      <StyledTableCell justifyContent="flex-start">
        <Box padding="xsmall">
          <Text size="standard">{contract.landlords}</Text>
        </Box>
      </StyledTableCell>
      <StyledTableCell justifyContent="flex-start">
        <Box padding="xsmall">
          <Text size="standard">
            {formatDate(contract.start_date, 'MMM Do, YYYY') ||
              t('b.components.view.not_avaiable.text', 'N/A')}
          </Text>
        </Box>
      </StyledTableCell>
      <StyledTableCell justifyContent="flex-start">
        <Box padding="xsmall">
          <Text size="standard">
            {formatDate(contract.end_date, 'MMM Do, YYYY') ||
              t('b.components.view.not_avaiable.text', 'N/A')}
          </Text>
        </Box>
      </StyledTableCell>
      <StyledTableCell justifyContent="flex-start">
        <Box padding="xsmall">
          <Text size="standard">{t(`b.contract.list.status_${contract.state_machine}.badge`)}</Text>
        </Box>
      </StyledTableCell>
      <StyledTableCell>
        <Box padding="xsmall">
          <ButtonLink
            target="_blank"
            href={`/chat`}
            onClick={e => {
              e.stopPropagation()
            }}
            variant="solid"
            state="default"
            size="small"
            label="Chat"
            iconPosition="leading"
            id={`rentals-table-chat-click-${contract.listing_contract_id}`}
          >
            {t('l.tenants.list.chat.text', 'Chat')}
          </ButtonLink>
        </Box>
      </StyledTableCell>
      {!isDesktop && (
        <StyledTableCell>
          <Box padding="xsmall">
            {!(contract.state_machine === 'sent' || contract.state_machine === 'active') ? (
              <TooltipRenderer
                tooltip={
                  <Box paddingY="xxxsmall" justifyContent="center">
                    <Text weight="medium" size="small">
                      {t(
                        'l.rentals.table.view_button_disabled.tooltip',
                        'You are not able to view this contract as it has been archived.'
                      )}
                    </Text>
                  </Box>
                }
              >
                {({ triggerProps }) => (
                  <Box aria-label="Clear Search" {...triggerProps} alignItems="center">
                    <Button
                      disabled
                      onClick={e => {
                        e.stopPropagation()
                      }}
                      variant="ghost"
                      state="default"
                      tone="default"
                      size="small"
                      label="View"
                      id={`rentals-table-view-click-${contract.listing_contract_id}`}
                    >
                      {t('l.listing.list.view.text', 'View')}
                    </Button>
                  </Box>
                )}
              </TooltipRenderer>
            ) : (
              <ButtonLink
                disabled={
                  !(contract.state_machine === 'sent' || contract.state_machine === 'active')
                }
                href={
                  contract.state_machine === 'sent'
                    ? `/rental/${contract.listing_id}/contract/${contract.listing_contract_id}`
                    : `/rental/${contract.listing_id}`
                }
                onClick={e => {
                  e.stopPropagation()
                }}
                variant="ghost"
                state="default"
                tone="default"
                size="small"
                label="View"
                id={`rentals-table-view-click-${contract.listing_contract_id}`}
              >
                {t('l.listing.list.view.text', 'View')}
              </ButtonLink>
            )}
          </Box>
        </StyledTableCell>
      )}
    </>
  )
}

// Actions
const EMPTY = 1
const LOADING_CONTRACTS = 2
const LOADED_CONTRACTS = 3
const EMPTY_RESULT = 4

// States
const NO_DATA = 1
const DATA_LOADING = 2
const DATA_LOADED = 3
const SEARCH_EMPTY = 4

const reducer = (prevState, action) => {
  switch (action) {
    case EMPTY:
      return NO_DATA

    case LOADING_CONTRACTS:
      return DATA_LOADING

    case LOADED_CONTRACTS:
      return DATA_LOADED

    case EMPTY_RESULT:
      return SEARCH_EMPTY

    default:
      return prevState
  }
}

const MyRentals = props => {
  const { t } = useTranslation()
  const session = useSelector(path(['session', 'session']))
  const [state, loadingDispatch] = useReducer(reducer, NO_DATA)
  const [data, setData] = useState([])
  const { visible, showModal, closeModal } = useDialogControllers({
    warningDialog: false
  })
  const [filteredContracts, setFilteredContracts] = useState([])
  const [currentLoadedLength, setCurrentLoadedLength] = useState(0)
  const [rowsLoading, setRowsLoading] = useState(false)
  const contracts = useSelector(select.contract.activeContracts)
  const dispatch = useDispatch()
  const logout = () => dispatch.auth.logout()
  const location = useLocation()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const isDesktop = useMediaQuery(`(max-width: ${breakpoints.desktop})`)

  const [currentSort, setCurrentSort] = useState({
    prop: 'none',
    direction: 'none'
  })

  const loadData = useCallback(() => {
    setCurrentLoadedLength(loadedLength => loadedLength + 20)
  }, [])

  useEffect(() => {
    if (!rowsLoading) return
    loadData()
  }, [loadData, rowsLoading])

  const onScroll = useMemo(
    () =>
      throttle(async event => {
        const el = event
        const scrollTop = el.target.children[0].scrollTop
        const scrollHeight = el.target.children[0].scrollHeight - el.target.children[0].clientHeight
        const ratio = scrollTop / scrollHeight
        if (ratio > 0.9 && currentLoadedLength < filteredContracts?.length) {
          setRowsLoading(true)
        }
      }, 300),
    [currentLoadedLength, filteredContracts?.length]
  )
  useEventListener('scroll', onScroll)

  const urlParams = new URLSearchParams(location.search)
  // Param rw for "renter warning"
  const modalParam = urlParams.get('rw')
  useEffect(() => {
    if (modalParam === '1') {
      return showModal('warningDialog')
    }
  }, [location.search, data])

  useEffect(() => {
    dispatch.contract.getContracts()
    loadingDispatch(LOADING_CONTRACTS)
  }, [dispatch.contract, session])

  useEffect(() => {
    const formattedContracts = groupWith((a, b) => a.building_name === b.building_name, contracts)
    const initialSortFn = sortStates('state_machine', 'asc')
    const initialSortedContracts = formattedContracts
      ?.map(contracts => orderContracts(contracts, 'state_machine', 'asc'))
      .sort((a, b) => initialSortFn(a[0], b[0]))
    if (initialSortedContracts.length > 0) {
      setCurrentLoadedLength(20)
      setData(initialSortedContracts)
      setFilteredContracts(initialSortedContracts)
      loadingDispatch(LOADED_CONTRACTS)
    } else {
      loadingDispatch(EMPTY)
    }
  }, [contracts])

  const goToRentalDashboard = contract => {
    if (!canViewDetail(contract.state_machine)) return
    contract.state_machine === 'sent'
      ? props.history.push(
          `/rental/${contract.listing_id}/contract/${contract.listing_contract_id}`
        )
      : props.history.push(`/rental/${contract.listing_id}`)
  }

  //Custom sort for contract states
  const sortStates = (prop, direction) => {
    const getNumberedState = state => {
      let newState = ''
      switch (state) {
        case 'active':
          newState = '0'
          break
        case 'sent':
          newState = '1'
          break
        case 'history':
          newState = '2'
          break
        default:
          break
      }
      return newState
    }
    if (prop === 'none') {
      prop = 'state_machine'
    }
    const sorted = (a, b) => {
      if (getNumberedState(a[prop]) > getNumberedState(b[prop])) {
        return direction === 'asc' || direction === 'none' ? 1 : -1
      } else if (getNumberedState(a[prop]) < getNumberedState(b[prop])) {
        return direction === 'asc' || direction === 'none' ? -1 : 1
      }
      return 0
    }
    return sorted
  }

  const orderContracts = useCallback(
    (contracts, prop, direction) => {
      switch (currentSort.prop) {
        case 'address':
        case 'landlords':
          contracts.sort(sortStrings(prop, direction))
          break
        case 'none':
        case 'state_machine':
          contracts.sort(sortStates(prop, direction))
          break
        case 'price':
          contracts.sort(sortNumbers(prop, direction))
          break
        case 'end_date':
        case 'start_date':
          contracts.sort(sortDates(prop, direction))
          break
        default:
          break
      }

      return contracts
    },
    [currentSort]
  )

  const getSortedData = useCallback(() => {
    let sortedData = filteredContracts
    switch (currentSort.prop) {
      case 'address':
      case 'landlords':
        const sortStringsFn = sortStrings(currentSort.prop, currentSort.direction)
        sortedData = filteredContracts
          ?.map(contracts => orderContracts(contracts, currentSort.prop, currentSort.direction))
          .sort((a, b) => sortStringsFn(a[0], b[0]))
        break
      case 'none':
      case 'state_machine':
        const sortStatesFn = sortStates(currentSort.prop, currentSort.direction)
        sortedData = filteredContracts
          ?.map(contracts => orderContracts(contracts, currentSort.prop, currentSort.direction))
          .sort((a, b) => sortStatesFn(a[0], b[0]))
        break
      case 'price':
        const sortNumbersFn = sortNumbers(currentSort.prop, currentSort.direction)
        sortedData = filteredContracts
          ?.map(contracts => orderContracts(contracts, currentSort.prop, currentSort.direction))
          .sort((a, b) => sortNumbersFn(a[0], b[0]))
        break
      case 'end_date':
      case 'start_date':
        const sortDatesFn = sortDates(currentSort.prop, currentSort.direction)
        sortedData = filteredContracts
          ?.map(contracts => orderContracts(contracts, currentSort.prop, currentSort.direction))
          .sort((a, b) => sortDatesFn(a[0], b[0]))
        break
      default:
        break
    }
    return sortedData
  }, [filteredContracts, currentSort.prop, currentSort.direction, orderContracts])

  const overallSortedData = useMemo(() => getSortedData(), [getSortedData])

  const updateCurrentSort = propName => {
    if (currentSort.direction === 'asc' && currentSort.prop === propName) {
      setCurrentSort({
        prop: propName,
        direction: 'desc'
      })
    } else if (currentSort.direction === 'desc' && currentSort.prop === propName) {
      setCurrentSort({
        prop: 'none',
        direction: 'none'
      })
    } else {
      setCurrentSort({
        prop: propName,
        direction: 'asc'
      })
    }
  }

  // Handle search
  const searchContracts = searchString => {
    const isSearchEmpty = searchString.trim().length === 0
    loadingDispatch(LOADING_CONTRACTS)

    const filtered = data
      .map(building => ({
        ...[building],
        contracts: building.filter(contract => {
          const searchFields = values(
            pick(
              ['address', 'unit_number', 'building_name', 'city', 'state', 'landlords'],
              contract
            )
          )
          return JSON.stringify(searchFields)
            .toLowerCase()
            .includes(searchString.trim().toLowerCase())
        })
      }))
      .filter(building => {
        return building.contracts && building.contracts.length > 0
      })
      .map(building => building[0])

    setFilteredContracts(filtered)

    if (!isSearchEmpty && filtered.length === 0) {
      loadingDispatch(EMPTY_RESULT)
    } else {
      loadingDispatch(LOADED_CONTRACTS)
    }
    if (typeof window !== 'undefined') window.scrollTo(0, 0)
  }

  useEffect(() => {
    setRowsLoading(false)
  }, [filteredContracts, currentLoadedLength])

  // Warning Modal
  // warningModaltitle: string, warningModalDescription: ReactNode, ctaText: string
  const warningModalTitle = t(
    'r.landlord.view.logged_in_as_renter.title',
    "Oops! It looks like you're logged in as a renter."
  )

  const warningModalDescription = (
    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column' }}>
      <span>
        {t(
          'r.landlord.view.logged_in_as_renter.text',
          'To access landlord-specific features, please log out and sign up for a landlord account or log in with your landlord account details.'
        )}
      </span>
      <br />
      <span>
        <Trans i18nKey="r.landlord.view.logged_in_as_renter.text_3">
          If you made this account in error, you can contact us at{' '}
          <a href="mailto:support@liv.rent" target="_blank" rel="noopener noreferrer">
            support@liv.rent
          </a>
          .
        </Trans>
      </span>
    </div>
  )

  return (
    <Wrapper>
      <WarningModal
        visible={visible.warningDialog}
        title={warningModalTitle}
        description={warningModalDescription}
        illustration={<WarningLarge />}
        ctaText={t('common.log_out', 'Log Out')}
        ctaAction={logout}
        onClose={closeModal('warningDialog')}
      />
      <Helmet>
        <title>{t('b.rentals.view.my_rentals.title', 'Your Rentals')}</title>
      </Helmet>
      <PageTitle>{t('b.rentals.view.my_rentals.title', 'Your Rentals')}</PageTitle>
      <MyRentalsHeader onSearch={searchContracts} />
      <TableOutterWrapper>
        <TableWrapper>
          <Table>
            {!isMobile && (
              <TableHeader>
                <TableRow
                  sticky
                  top={!isMobile ? structure.header.height + 118 : structure.header.height + 152}
                >
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('address')
                    }}
                    active={currentSort.prop === 'address'}
                    ascending={currentSort.direction === 'asc'}
                    width="28%"
                  >
                    <TableHeaderText alignLeft>
                      {t('common_header.unit_address', 'Unit Address')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('price')
                    }}
                    active={currentSort.prop === 'price'}
                    ascending={currentSort.direction === 'asc'}
                    width="12%"
                  >
                    <TableHeaderText alignLeft>{t('common.price', 'Price')}</TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('landlords')
                    }}
                    active={currentSort.prop === 'landlords'}
                    ascending={currentSort.direction === 'asc'}
                    width="16%"
                  >
                    <TableHeaderText alignLeft>
                      {t('b.rentals.view.landlord.text', 'Landlord')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('start_date')
                    }}
                    active={currentSort.prop === 'start_date'}
                    ascending={currentSort.direction === 'asc'}
                    width="14%"
                  >
                    <TableHeaderText alignLeft>
                      {t('b.rentals.view.lease_start.text', 'Lease Start')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('end_date')
                    }}
                    active={currentSort.prop === 'end_date'}
                    ascending={currentSort.direction === 'asc'}
                    width="14%"
                  >
                    <TableHeaderText alignLeft>
                      {t('b.rentals.view.lease_end.text', 'Lease End')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('state_machine')
                    }}
                    active={currentSort.prop === 'state_machine'}
                    ascending={currentSort.direction === 'asc'}
                    width="16%"
                  >
                    <TableHeaderText alignLeft>
                      {t('l.rentals.table.contract_status.text', 'Contract Status')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell width="89px">
                    <TableHeaderText></TableHeaderText>
                  </TableHeaderCell>
                  {!isDesktop && (
                    <TableHeaderCell width="90px">
                      <TableHeaderText></TableHeaderText>
                    </TableHeaderCell>
                  )}
                </TableRow>
              </TableHeader>
            )}
            <TableBodyContainer>
              {state === DATA_LOADED &&
                overallSortedData?.slice(0, currentLoadedLength).map((building, index) => {
                  if (building.length === 1) {
                    const contract = building[0]
                    const state = building[0].state_machine
                    return (
                      <SingleTableRow
                        key={`Contract - ${index}`}
                        state={state}
                        onClick={() => goToRentalDashboard(contract)}
                      >
                        {isMobile ? (
                          <RenderMobileRentalRow contract={contract} />
                        ) : (
                          <RenderRentalRow contract={contract} />
                        )}
                      </SingleTableRow>
                    )
                  } else {
                    return (
                      <Accordion key={`${building[0]}-${index}`} expanded>
                        <AccordionHeader
                          colSpan={isDesktop && !isMobile ? 7 : isMobile ? 9 : 8}
                          listingsView={!isMobile}
                          unitCount={building.length}
                          isMobile={isMobile}
                        >
                          {getBuildingTitle(building)}
                        </AccordionHeader>
                        <AccordionContent>
                          <>
                            {building.map((contract, index) => {
                              return (
                                <AccordionTableRow onClick={() => goToRentalDashboard(contract)}>
                                  {isMobile ? (
                                    <RenderMobileRentalRow groupedListing contract={contract} />
                                  ) : (
                                    <RenderRentalRow groupedListing contract={contract} />
                                  )}
                                </AccordionTableRow>
                              )
                            })}
                          </>
                        </AccordionContent>
                      </Accordion>
                    )
                  }
                })}
              {state === SEARCH_EMPTY && (
                <TableRow>
                  <StyledTableCell colSpan={8}>
                    <NoRentalsComponent />
                  </StyledTableCell>
                </TableRow>
              )}
              {state === EMPTY && (
                <TableRow>
                  <StyledTableCell colSpan={8}>
                    <RentalsEmptyListComponent />
                  </StyledTableCell>
                </TableRow>
              )}
              {state === DATA_LOADING && <SkeletonState />}
            </TableBodyContainer>
          </Table>
        </TableWrapper>
      </TableOutterWrapper>
      {rowsLoading && <LoadingState />}
    </Wrapper>
  )
}

export default MyRentals
